import React from "react";
import { Zap, Flashlight, ShieldAlert, Package } from "lucide-react";
import { NumberField } from "@/components/calc/NumberField";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CURRENCIES, DEVICE_HZ_PRESETS } from "@/lib/calc";

const Section = ({ icon: Icon, title, subtitle, color, children }) => (
  <Card className="rounded-2xl border bg-card p-5 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div
        className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}1a`, color }}
      >
        <Icon className="h-4.5 w-4.5" style={{ height: 18, width: 18 }} />
      </div>
      <div>
        <h3 className="font-heading font-semibold text-base leading-tight">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    {children}
  </Card>
);

export const Parameters = ({ params, set, currency }) => {
  const sym = (CURRENCIES[currency] || CURRENCIES.USD).symbol;
  return (
    <div className="space-y-4">
      <Section icon={Zap} title="Electricity &amp; firing rate" subtitle="Machine kW × your price, costed by firing time" color="#7A8B96">
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Rate" prefix={sym} unit="/kWh" value={params.electricityRate} onChange={(v) => set("electricityRate", v)} testId="input-electricity-rate" hint="YOUR clinic's electricity price per kWh — check a recent power bill." />
          <NumberField label="Power Draw" unit="kW" value={params.powerDraw} onChange={(v) => set("powerDraw", v)} testId="input-power-draw" hint="The laser's total power input in kW, from the machine's rating/spec plate (e.g. Polylase ≈ 4 kW). Or volts × amps ÷ 1000." />
        </div>
        <div className="mt-3">
          <label className="font-mono-label text-[11px] uppercase tracking-wider text-muted-foreground">Quick-pick device rep rate (optional)</label>
          <Select value="" onValueChange={(v) => set("repRate", parseFloat(v))}>
            <SelectTrigger className="h-10 rounded-lg mt-1" data-testid="rep-rate-preset"><SelectValue placeholder="Choose a common device…" /></SelectTrigger>
            <SelectContent>
              {DEVICE_HZ_PRESETS.map((d) => <SelectItem key={d.name} value={String(d.repRate)}>{d.name} — {d.repRate} Hz</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="mt-3">
          <NumberField label="Rep rate (firing speed)" unit="Hz" value={params.repRate} onChange={(v) => set("repRate", v)} testId="input-rep-rate" hint="Pulses fired per second while treating. Sets how much firing time — and therefore electricity — a shot count uses. Adjust to your device and settings." />
        </div>
      </Section>

      <Section icon={Flashlight} title="Flashlamp Servicing" subtitle="Regular maintenance, per pulse count" color="#3E4552">
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Replacement Cost" prefix={sym} value={params.flashlampCost} onChange={(v) => set("flashlampCost", v)} testId="input-flashlamp-cost" hint="Cost to replace the flashlamp, including labour." />
          <NumberField label="Lamp Lifespan" unit="pulses" value={params.flashlampLifespan} onChange={(v) => set("flashlampLifespan", v)} testId="input-flashlamp-lifespan" hint="Rated pulses per flashlamp before it needs replacing — differs for every device (check the manufacturer spec)." />
        </div>
      </Section>

      <Section icon={ShieldAlert} title="Major Failure Reserve" subtitle="Catastrophic repair: pump chamber / laser head" color="#B5704F">
        <div className="grid grid-cols-2 gap-3">
          <NumberField label="Repair Cost / Failure" prefix={sym} value={params.majorServiceCost} onChange={(v) => set("majorServiceCost", v)} testId="input-major-service-cost" hint="What ONE major failure costs to repair (pump chamber, laser head) — often around $17,000." />
          <NumberField label="Spread Over" unit="years" value={params.reserveYears} onChange={(v) => set("reserveYears", v)} testId="input-reserve-years" hint="Over how many years you set the repair aside. Reserve/yr = repair cost ÷ years (e.g. $17,000 ÷ 4 = $4,250/yr)." />
        </div>
        <div className="mt-3">
          <NumberField label="Your shots per year" unit="shots" value={params.annualShots} onChange={(v) => set("annualShots", v)} testId="input-annual-shots" hint="Roughly how many shots this machine fires per year — depends on YOUR volume. Used to spread the yearly reserve onto a per-shot basis (reserve/yr ÷ shots/yr). Adjust to your clinic." />
        </div>
        <div className="flex items-center justify-between rounded-lg border border-border p-3 mt-3">
          <div>
            <div className="font-medium text-sm">Currently under warranty</div>
            <div className="text-xs text-muted-foreground">If covered, no reserve is needed right now</div>
          </div>
          <Switch checked={!!params.underWarranty} onCheckedChange={(v) => set("underWarranty", v)} data-testid="under-warranty-toggle" />
        </div>
        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
          A rainy-day fund: Reserve/yr = repair cost ÷ years, set aside monthly. For the "cost for a number of shots" lookup it's also spread per shot (reserve/yr ÷ your shots/yr), so every shot carries its share of a future major repair.
        </p>
      </Section>

      <Section icon={Package} title="Consumables (optional)" subtitle="Per-shot disposables" color="#A38059">
        <NumberField label="Consumables per shot" prefix={sym} value={params.consumablesPerShot} onChange={(v) => set("consumablesPerShot", v)} testId="input-consumables-per-shot" hint="Optional per-shot cost of disposables that scale with firing. Most consumables (gel, eyewear per treatment) belong in the Treatment Builder — leave 0 if you price them there." />
      </Section>
    </div>
  );
};
